import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { mediaUrl } from '../api';
import { fromFrame, PLUGIN_ORIGIN, pluginText, pluginUrl } from './pluginFrame';

/**
 * A plugin's editor page in a frame (documentation/plugins.md §7).
 *
 * The page says it is ready and is handed the block being edited (null for a
 * new one) with the addresses of its stored files; every change comes back as
 * `onChange(data, media)`, where `media` names files the page hands over to be
 * stored. `initial` and `scope` are read once: the page owns the data while it
 * is open, and a new scope would reload it.
 */
function PluginEditor({ plugin, initial = null, scope = null, onChange, disabled = false }) {
  const { t, i18n } = useTranslation();
  const frame = useRef(null);
  const start = useRef(initial);
  const [startScope] = useState(scope);
  const change = useRef(onChange);
  const [ready, setReady] = useState(false);
  // The page's own height (disi:height): the frame grows with it, so the page has the only scrollbar.
  const [height, setHeight] = useState(null);
  const name = pluginText(plugin.name, i18n.language);

  change.current = onChange;

  useEffect(() => {
    function onMessage(event) {
      if (!fromFrame(event, frame.current)) return;

      if (event.data.type === 'disi:ready') {
        const data = start.current;
        const media = {};
        for (const field of plugin.block.media ?? []) {
          if (typeof data?.[field] === 'string' && data[field]) media[field] = mediaUrl(data[field]);
        }
        frame.current.contentWindow.postMessage(
          { type: 'disi:load', data, media, lang: i18n.language },
          PLUGIN_ORIGIN,
        );
        setReady(true);
      }

      if (event.data.type === 'disi:height' && Number.isFinite(event.data.height)) {
        setHeight(Math.max(320, Math.min(event.data.height, 20000)));
      }

      if (event.data.type === 'disi:changed' && event.data.data !== undefined) {
        change.current(event.data.data, event.data.media ?? null);
      }
    }

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [i18n.language, plugin]);

  return (
    <div className={`plugin-editor${disabled ? ' plugin-editor--disabled' : ''}`}>
      {!ready && <p className="plugin-editor__loading" role="status">{t('plugins.loading', { name })}</p>}
      <iframe
        ref={frame}
        src={pluginUrl(plugin.id, startScope, plugin.block.editor)}
        title={name}
        className="plugin-editor__frame"
        style={height ? { height } : undefined}
      />
    </div>
  );
}

export default PluginEditor;
